import TableColumnClickableText from '@/components/ui/table/TableColumnClickableText';
import TableColumnDate from '@/components/ui/table/TableColumnDate';
import type { Issue } from '@/types';
import { List } from '@bitstopco/bitstop-theme';
import type { Columns } from '@bitstopco/bitstop-theme';
import { ClickableText } from '@bitstopco/bitstop-theme';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';

interface IssueDetailsTabProps {
  issue: Issue;
  hide: () => void;
}

const IssueDetailsTab = ({ issue }: IssueDetailsTabProps) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'active':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      default:
        return <WarningIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <WarningIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      case 'low':
        return <CheckCircleIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
      default:
        return <WarningIcon sx={{ fontSize: 20 }} />;
    }
  };

  const listConfig = useMemo(
    (): Columns<Issue> => ({
      defaultValue: '-',
      valueSx: {
        textAlign: 'right',
      },
      items: [
        {
          label: 'Display ID',
          value: 'display_id',
          valueMapper: (value: Issue['display_id']) =>
            value && <Typography variant="body2">{value}</Typography>,
        },
        {
          label: 'Issue ID',
          value: 'id',
          valueMapper: (value: Issue['id']) =>
            value && (
              <ClickableText
                fixIcon
                text={value}
                name="Issue ID"
                clipboardValue={value}
                sx={{
                  '& span': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-all',
                  },
                }}
              />
            ),
        },
        {
          label: 'Issue Type',
          value: 'issue_type',
          valueMapper: (value: Issue['issue_type']) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <span style={{ textTransform: 'capitalize' }}>
                {value.replace(/([A-Z])/g, ' $1').trim()}
              </span>
            </Box>
          ),
        },
        {
          label: 'Severity',
          value: 'severity',
          valueMapper: (value: Issue['severity']) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <span style={{ textTransform: 'capitalize' }}>{value}</span>
              {getSeverityIcon(value)}
            </Box>
          ),
        },
        {
          label: 'Status',
          value: 'status',
          valueMapper: (value: Issue['status']) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <span style={{ textTransform: 'capitalize' }}>{value}</span>
              {getStatusIcon(value)}
            </Box>
          ),
        },
        {
          label: 'Detected At',
          value: 'detected_at',
          valueMapper: (value: Issue['detected_at']) => value && <TableColumnDate value={value} />,
        },
        {
          label: 'Created At',
          value: 'created_at',
          valueMapper: (value: Issue['created_at']) => value && <TableColumnDate value={value} />,
        },
        {
          label: 'Last Updated At',
          value: 'last_updated_at',
          valueMapper: (value: Issue['last_updated_at']) =>
            value && <TableColumnDate value={value} />,
        },
        ...(issue.resolved_at
          ? [
              {
                label: 'Resolved At',
                value: 'resolved_at',
                valueMapper: (value: Issue['resolved_at']) =>
                  value && <TableColumnDate value={value} />,
              },
            ]
          : []),
        {
          label: 'Machine',
          value: 'atm_id',
          valueMapper: (value: Issue['atm_id'], issueData: Issue) =>
            value && (
              <a href={`/machine/${value}`}>
                <Typography variant="body2">{issueData.atm_id}</Typography>
              </a>
            ),
        },
        {
          label: 'Location ID',
          value: 'location_id',
          valueMapper: (value: Issue['location_id'], issueData: Issue) => {
            if (!value) return '-';
            const location = issueData.location;

            // If we have location details, show address
            if (location) {
              const { full_address, street_address, city, state, zipcode } = location;
              const zip = zipcode;

              if (full_address) {
                return (
                  <TableColumnClickableText
                    id={String(value)}
                    text={full_address}
                    to={`/location/${value}`}
                    clipboardValue={full_address}
                    name="Location Address"
                    overrideTooltipLinkText="Go to Location"
                  />
                );
              }

              if (street_address || city) {
                const address = [street_address, city, state, zip].filter(Boolean).join(', ');
                return (
                  <ClickableText
                    fixIcon
                    text={address}
                    name="Location Address"
                    clipboardValue={address}
                    to={`/location/${value}`}
                    sx={{
                      '& span': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      },
                    }}
                  />
                );
              }
            }

            // Fallback to location ID
            return (
              <ClickableText
                fixIcon
                text={`Location ${value}`}
                name="Location ID"
                clipboardValue={value.toString()}
                to={`/location/${value}`}
                sx={{
                  '& span': {
                    whiteSpace: 'normal',
                  },
                }}
              />
            );
          },
        },
        {
          label: 'Auto Case Created',
          value: 'auto_case_created',
          valueMapper: (value: Issue['auto_case_created']) => (value ? 'Yes' : 'No'),
        },
        {
          label: 'Created By',
          value: 'created_by',
        },
        {
          label: 'Last Updated By',
          value: 'last_updated_by',
        },
        ...(issue.linked_case_id
          ? [
              {
                label: 'Linked Case ID',
                value: 'linked_case_id',
                valueMapper: (value: Issue['linked_case_id']) =>
                  value && (
                    <ClickableText
                      text={value?.toString()}
                      name="Linked Case ID"
                      clipboardValue={value}
                      to={`/case/${value}`}
                    />
                  ),
              },
            ]
          : []),
        ...(issue.linked_service_ticket_id
          ? [
              {
                label: 'Linked Service Ticket ID',
                value: 'linked_service_ticket_id',
                valueMapper: (value: Issue['linked_service_ticket_id']) =>
                  value && (
                    <ClickableText
                      fixIcon
                      text={value}
                      name="Linked Service Ticket ID"
                      clipboardValue={value}
                      sx={{
                        '& span': {
                          whiteSpace: 'normal',
                          wordBreak: 'break-all',
                        },
                      }}
                    />
                  ),
              },
            ]
          : []),
        ...(issue.notes
          ? [
              {
                label: 'Notes',
                value: 'notes',
              },
            ]
          : []),
      ],
    }),
    [issue],
  );

  return (
    <>
      <List<Issue>
        data={issue}
        columns={listConfig}
        sx={{ px: 1 }}
        listItemProps={{
          sx: {
            '& > div': {
              width: '100%',
              '& > div:first-of-type': {
                width: '40%',
              },
              '& > div': {
                margin: 0,
              },
            },
          },
        }}
      />
    </>
  );
};

export default IssueDetailsTab;
